import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, RotateCcw } from "lucide-react";

const FORMATS = ["3v3", "5v5", "7v7", "9v9", "11v11"];
const BALL_SIZES = ["3", "4", "5"];
const emptyRule = { age_group: "", game_format: "7v7", ball_size: "4", duration_min: 60, halves: "", goal_size: "", pitch_size: "" };

export default function AdminFARulesEditor({ onSaved }) {
  const [rules, setRules] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [rulesSaving, setRulesSaving] = useState(false);

  const [mapEntries, setMapEntries] = useState([]);
  const [teams, setTeams] = useState([]);
  const [mapLoading, setMapLoading] = useState(true);
  const [mapSaving, setMapSaving] = useState(false);

  const load = useCallback(async () => {
    setRulesLoading(true); setMapLoading(true);
    try {
      const [r, m, t] = await Promise.all([
        api.get("/fa/age-rules"),
        api.get("/fa/team-map"),
        api.get("/teams"),
      ]);
      setRules(r.data.rules); setDefaults(r.data.defaults);
      setMapEntries(m.data.entries);
      setTeams(t.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load FA settings");
    } finally {
      setRulesLoading(false); setMapLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateRule = (i, patch) => setRules(prev => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const removeRule = (i) => setRules(prev => prev.filter((_, idx) => idx !== i));
  const addRule = () => setRules(prev => [...prev, { ...emptyRule, age_group: "U" }]);
  const resetRules = () => setRules(defaults);

  const saveRules = async () => {
    setRulesSaving(true);
    try {
      const cleaned = rules
        .filter(r => r.age_group?.trim())
        .map(r => ({
          age_group: r.age_group.trim(),
          game_format: r.game_format,
          ball_size: String(r.ball_size),
          duration_min: parseInt(r.duration_min) || 60,
          halves: r.halves?.trim() || null,
          goal_size: r.goal_size?.trim() || null,
          pitch_size: r.pitch_size?.trim() || null,
        }));
      const { data } = await api.put("/fa/age-rules", { rules: cleaned });
      setRules(data.rules);
      toast.success(`Saved ${cleaned.length} FA age-group rule${cleaned.length === 1 ? "" : "s"}`);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setRulesSaving(false); }
  };

  const updateMap = (i, patch) => setMapEntries(prev => prev.map((e, idx) => idx === i ? { ...e, ...patch } : e));
  const removeMap = (i) => setMapEntries(prev => prev.filter((_, idx) => idx !== i));
  const addMap = () => setMapEntries(prev => [...prev, { fa_name: "", team_id: "" }]);

  const saveMap = async () => {
    setMapSaving(true);
    try {
      const cleaned = mapEntries
        .filter(e => e.fa_name?.trim() && e.team_id)
        .map(e => ({ fa_name: e.fa_name.trim(), team_id: e.team_id }));
      const { data } = await api.put("/fa/team-map", { entries: cleaned });
      toast.success(`Saved ${data.count} FA→team mapping${data.count === 1 ? "" : "s"}`);
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setMapSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="fa-rules-editor">
      {/* ---- Age Rules ---- */}
      <div className="card-tactical rounded-md overflow-hidden">
        <div className="p-5 border-b border-border flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <div className="font-display font-bold text-xl tracking-tight">FA Age-Group Rules</div>
            <p className="text-xs opacity-60 mt-1">Game format, ball size and duration per age group. Feeds the FA import so imported fixtures get the right kick-off length and pitch.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={resetRules}
              disabled={rulesSaving}
              data-testid="fa-rules-reset"
              className="text-[10px] tracking-widest uppercase px-3 py-2 border border-border hover:border-amber-400 hover:text-amber-300 rounded inline-flex items-center gap-1.5 transition-colors disabled:opacity-40"
            >
              <RotateCcw className="w-3 h-3" /> Reset defaults
            </button>
            <button
              type="button"
              onClick={saveRules}
              disabled={rulesSaving || rulesLoading}
              data-testid="fa-rules-save"
              className="text-[10px] tracking-widest uppercase px-4 py-2 rounded inline-flex items-center gap-2 transition-colors disabled:opacity-50 bg-[#83B68B] text-black hover:bg-[#83B68B] font-bold"
            >
              {rulesSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Save rules
            </button>
          </div>
        </div>
        {rulesLoading ? (
          <div className="p-8 text-sm opacity-60 text-center"><Loader2 className="inline w-4 h-4 mr-2 animate-spin" /> Loading…</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-[10px] tracking-widest uppercase opacity-70">
                  <tr>
                    <th className="text-left px-4 py-2">Age group</th>
                    <th className="text-left px-4 py-2">Game format</th>
                    <th className="text-left px-4 py-2">Ball size</th>
                    <th className="text-left px-4 py-2">Duration (min)</th>
                    <th className="text-left px-4 py-2">Halves</th>
                    <th className="text-left px-4 py-2">Goal size</th>
                    <th className="text-left px-4 py-2">Pitch size</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((r, i) => (
                    <tr key={r.age_group || i} className="border-t border-border" data-testid={`fa-rule-row-${r.age_group}`}>
                      <td className="px-3 py-2 w-24"><Input value={r.age_group} onChange={e => updateRule(i, { age_group: e.target.value })} className="h-8 font-mono text-xs" /></td>
                      <td className="px-3 py-2 w-32">
                        <Select value={r.game_format} onValueChange={v => updateRule(i, { game_format: v })}>
                          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>{FORMATS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2 w-24">
                        <Select value={String(r.ball_size)} onValueChange={v => updateRule(i, { ball_size: v })}>
                          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>{BALL_SIZES.map(b => <SelectItem key={b} value={b}>Size {b}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2 w-28"><Input type="number" min="10" max="180" value={r.duration_min} onChange={e => updateRule(i, { duration_min: e.target.value })} className="h-8" /></td>
                      <td className="px-3 py-2 w-28"><Input value={r.halves || ""} onChange={e => updateRule(i, { halves: e.target.value })} placeholder="2x30" className="h-8 font-mono text-xs" /></td>
                      <td className="px-3 py-2 w-32"><Input value={r.goal_size || ""} onChange={e => updateRule(i, { goal_size: e.target.value })} placeholder="21x7 ft" className="h-8 font-mono text-xs" /></td>
                      <td className="px-3 py-2 w-32"><Input value={r.pitch_size || ""} onChange={e => updateRule(i, { pitch_size: e.target.value })} placeholder="90x55 yd" className="h-8 font-mono text-xs" /></td>
                      <td className="px-3 py-2">
                        <button type="button" onClick={() => removeRule(i)} data-testid={`fa-rule-remove-${r.age_group}`} className="p-1 rounded hover:bg-red-500/10 text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-3 border-t border-border">
              <button type="button" onClick={addRule} data-testid="fa-rule-add" className="text-[10px] tracking-widest uppercase px-3 py-2 border border-dashed border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-1.5">
                <Plus className="w-3 h-3" /> Add rule
              </button>
            </div>
          </>
        )}
      </div>

      {/* ---- Team Map ---- */}
      <div className="card-tactical rounded-md overflow-hidden">
        <div className="p-5 border-b border-border flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <div className="font-display font-bold text-xl tracking-tight">FA → Internal team map</div>
            <p className="text-xs opacity-60 mt-1">Map the exact name that appears in FA Full-Time to one of your club squads. Overrides fuzzy matching during preview.</p>
          </div>
          <button
            type="button"
            onClick={saveMap}
            disabled={mapSaving || mapLoading}
            data-testid="fa-map-save"
            className="text-[10px] tracking-widest uppercase px-4 py-2 rounded inline-flex items-center gap-2 transition-colors disabled:opacity-50 bg-[#83B68B] text-black hover:bg-[#83B68B] font-bold"
          >
            {mapSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save map
          </button>
        </div>
        {mapLoading ? (
          <div className="p-8 text-sm opacity-60 text-center"><Loader2 className="inline w-4 h-4 mr-2 animate-spin" /> Loading…</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-[10px] tracking-widest uppercase opacity-70">
                  <tr>
                    <th className="text-left px-4 py-2">FA Full-Time name</th>
                    <th className="text-left px-4 py-2">→ Club squad</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {mapEntries.length === 0 && (
                    <tr><td colSpan="3" className="px-4 py-6 text-center text-xs opacity-50">No mappings yet — add one below or resolve unmatched names during FA import preview.</td></tr>
                  )}
                  {mapEntries.map((e, i) => (
                    <tr key={i} className={`border-t border-border ${e.stale ? "bg-amber-500/5" : ""}`} data-testid={`fa-map-row-${i}`}>
                      <td className="px-3 py-2"><Input value={e.fa_name || ""} onChange={ev => updateMap(i, { fa_name: ev.target.value })} className="h-8" placeholder="Elsenham U10 Rangers" /></td>
                      <td className="px-3 py-2">
                        <Select value={e.team_id || ""} onValueChange={v => updateMap(i, { team_id: v })}>
                          <SelectTrigger className="h-8" data-testid={`fa-map-team-${i}`}><SelectValue placeholder="Pick a squad" /></SelectTrigger>
                          <SelectContent>{teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}{t.age_group ? ` · ${t.age_group}` : ""}</SelectItem>)}</SelectContent>
                        </Select>
                        {e.stale && <div className="text-[10px] text-amber-400 mt-1">Mapped squad no longer exists</div>}
                      </td>
                      <td className="px-3 py-2">
                        <button type="button" onClick={() => removeMap(i)} data-testid={`fa-map-remove-${i}`} className="p-1 rounded hover:bg-red-500/10 text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-3 border-t border-border">
              <button type="button" onClick={addMap} data-testid="fa-map-add" className="text-[10px] tracking-widest uppercase px-3 py-2 border border-dashed border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-1.5">
                <Plus className="w-3 h-3" /> Add mapping
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
